import React, { useEffect, useRef, useState } from "react";
import { Loader2, ArrowRight, CheckCircle2, X, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import HabitLoopDiagram from "../habits/HabitLoopDiagram";
import { addIfThenBlock } from "../../lib/habitLoopPdfExtras";

// Suppression key — once a visitor either submits or dismisses the popup
// we won't bother them again for this long. Tuned conservatively: 7
// days lets a bounce-then-return visitor see the offer twice in the
// rough window where they're still in the "evaluating" phase, but not
// every single tab they open in the same session.
const SUPPRESSION_KEY = "fhts:habit_loop_popup_suppressed_until";
const SUPPRESSION_DAYS = 7;

// Scroll threshold for the secondary trigger. 50% of scrollable height
// is where most landing-page bounces happen — late enough that the
// visitor read more than the hero, early enough that we haven't lost
// them to the back button yet.
const SCROLL_THRESHOLD_PCT = 0.5;

// Mouse-out (exit-intent) tolerance — fires only when the cursor leaves
// through the top of the viewport (where the back/close buttons live).
// Pure y<=0 would be too strict; this catches the natural swoop-up.
const EXIT_INTENT_Y_THRESHOLD = 0;

function _isSuppressed() {
  if (typeof window === "undefined") return false;
  try {
    const until = Number(window.localStorage.getItem(SUPPRESSION_KEY) || "0");
    return until > Date.now();
  } catch {
    return false;
  }
}

function _markSuppressed() {
  if (typeof window === "undefined") return;
  try {
    const until = Date.now() + SUPPRESSION_DAYS * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(SUPPRESSION_KEY, String(until));
  } catch {
    // Private mode or storage disabled — accept that we may re-show.
  }
}

/**
 * Exit-intent + scroll-50% Habit Loop lead-magnet popup.
 *
 * Triggers (whichever fires first):
 *   1. Mouse leaves the viewport through the top edge (classic exit-intent).
 *   2. Visitor scrolls past 50% of the page height.
 *
 * Suppression rules:
 *   - 7-day localStorage flag after submit OR dismiss → never twice in a
 *     row for the same visitor.
 *   - Once-per-page-load guarded via a ref → no re-trigger after dismissing.
 *   - Logged-in users skip entirely (the popup is for bounce prevention,
 *     not for converting members).
 *
 * Lead capture endpoint: POST /api/habit-loop/claim-pdf (same as the
 * inline magnet on the landing page) with ref="exit_intent" so the
 * admin Lead Funnel dashboard can attribute these signups separately
 * from the inline magnet.
 */
export default function ExitIntentHabitLoopPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  // Guards re-firing within the same page load.
  const firedRef = useRef(false);

  useEffect(() => {
    if (_isSuppressed()) return undefined;

    const tryOpen = (reason) => {
      if (firedRef.current) return;
      firedRef.current = true;
      // Tag the popup with the trigger source for attribution.
      window.__habitLoopPopupTrigger = reason;
      setOpen(true);
    };

    const onMouseOut = (e) => {
      // Only fire on cursor leaving through the top — moving the mouse
      // off-screen sideways is normal multi-monitor behavior.
      if (e.relatedTarget) return;
      if (e.clientY > EXIT_INTENT_Y_THRESHOLD) return;
      tryOpen("exit_intent");
    };
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY || doc.scrollTop || 0;
      const total = Math.max(1, (doc.scrollHeight || 0) - (window.innerHeight || 0));
      const pct = scrolled / total;
      if (pct >= SCROLL_THRESHOLD_PCT) {
        tryOpen("scroll_50");
      }
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // ESC closes once open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleDismiss = () => {
    setOpen(false);
    _markSuppressed();
  };

  const triggerLocalPdfDownload = async () => {
    // Mirrors HabitLoopLeadMagnet's PDF path so the popup user gets the
    // same printable file. Best-effort — the lead has already been
    // captured by the backend by the time we get here.
    try {
      const { jsPDF } = await import("jspdf");
      const ReactDOMServer = await import("react-dom/server");
      const svgString = ReactDOMServer.renderToStaticMarkup(<HabitLoopDiagram />);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const scale = 3;
      const size = 600 * scale;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        };
        img.src = url;
      });
      const pngDataUrl = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: "letter",
      });
      const imgSize = 6.5;
      const x = (8.5 - imgSize) / 2;
      pdf.addImage(pngDataUrl, "PNG", x, 1.0, imgSize, imgSize);
      addIfThenBlock(pdf);
      pdf.setFontSize(9);
      pdf.setTextColor(120, 118, 113);
      pdf.text(
        "Print, pin it, build the loop. — financialhabitstosucceed.com",
        4.25, 10.5, { align: "center" },
      );
      pdf.save("finhabits-habit-loop.pdf");
    } catch (e) {
      console.error("Exit-intent PDF download failed:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const trigger = (typeof window !== "undefined" && window.__habitLoopPopupTrigger) || "exit_intent";
      await api.post("/habit-loop/claim-pdf", {
        email: email.trim(),
        name: name.trim() || null,
        subscribe_blog: true,
        // Tag the lead row with the popup trigger so the admin Lead
        // Funnel chart can split exit-intent vs scroll-50 vs the
        // inline magnet.
        ref: trigger,
      });
      triggerLocalPdfDownload();
      try {
        const { trackLead } = await import("../../lib/metaPixel");
        trackLead({
          content_name: "Habit Loop Printable (popup)",
          content_category: "lead-magnet-popup",
        });
      } catch {
        // pixel unavailable — no-op
      }
      _markSuppressed();
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-popup-title"
    >
      <div className="relative w-full max-w-2xl bg-[#FBFAF6] border border-[#E6E3DB] shadow-2xl overflow-hidden">
        {/* Close button — top-right. Dismissing also suppresses for 7 days. */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-[#787671] hover:text-[#2C302E] hover:bg-[#EAE7DF] rounded-sm transition-colors z-10"
          aria-label="Close"
          data-testid="exit-intent-popup-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Diagram preview — left (hidden on mobile to keep the popup short). */}
          <div className="hidden md:flex md:col-span-2 bg-[#F2EFE7] border-r border-[#E6E3DB] p-5 items-center justify-center">
            <div className="w-full">
              <HabitLoopDiagram />
            </div>
          </div>

          {/* Form / success — right */}
          <div className="md:col-span-3 p-6 md:p-8">
            {done ? (
              <div data-testid="exit-intent-popup-success">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#3E6B37] mt-0.5 flex-shrink-0" />
                  <div>
                    <h3
                      id="exit-intent-popup-title"
                      className="font-serif text-2xl text-[#2C302E] leading-tight"
                    >
                      It&apos;s on the way.
                    </h3>
                    <p className="text-sm text-[#4d4f4c] mt-2 leading-relaxed">
                      Check <strong>{email}</strong> for your printable Habit
                      Loop. We&apos;ve also kicked off the download in this tab.
                    </p>
                    <a
                      href="/quiz?ref=exit-intent-popup"
                      className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#C05C3D] hover:text-[#A34A2E]"
                      data-testid="exit-intent-popup-quiz-cta"
                    >
                      <Sparkles className="h-4 w-4" />
                      Match a starter loop to me — take the 5-min quiz
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="exit-intent-popup-form">
                <p className="eyebrow text-[#C05C3D] mb-2">Wait — before you go</p>
                <h3
                  id="exit-intent-popup-title"
                  className="font-serif text-3xl text-[#2C302E] leading-tight"
                >
                  Take the loop with you.
                </h3>
                <p className="mt-3 text-sm text-[#4d4f4c] leading-relaxed">
                  Free, one-page printable. Pin it where you check your
                  money — Cue, Routine, Reward, with your Vision at the center.
                  Drop your email and the PDF&apos;s yours.
                </p>

                <label className="block text-sm font-medium text-[#2C302E] mt-5 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 border border-[#E6E3DB] bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#C05C3D] focus:border-transparent"
                  data-testid="exit-intent-popup-email"
                />
                <label className="block text-sm font-medium text-[#2C302E] mt-3 mb-1">
                  First name <span className="text-[#9b9892] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-3 py-2.5 border border-[#E6E3DB] bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#C05C3D] focus:border-transparent"
                  data-testid="exit-intent-popup-name"
                />

                {error && (
                  <p
                    className="mt-3 p-2.5 text-sm bg-[#FFF6F1] border border-[#C05C3D] text-[#C05C3D] rounded-sm"
                    data-testid="exit-intent-popup-error"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm bg-[#C05C3D] text-white hover:bg-[#A34A2E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  data-testid="exit-intent-popup-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send me the Habit Loop
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="mt-3 w-full text-center text-xs text-[#787671] hover:text-[#2C302E] underline"
                  data-testid="exit-intent-popup-no-thanks"
                >
                  No thanks
                </button>
                <p className="mt-3 text-[11px] text-[#9b9892] leading-relaxed text-center">
                  One email. PDF + a weekly read. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
